import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@shared/api/client';

export interface PollOptionDto {
  id: string;
  label: { it?: string; en?: string };
  voteCount: number;
}

export interface PollDto {
  id: string;
  question: { it?: string; en?: string };
  options: PollOptionDto[];
  totalVotes: number;
  openAt: string | null;
  closeAt: string | null;
  matchId: string | null;
  myVoteOptionId: string | null;
  isActive: boolean;
}

export interface PollOptionInput {
  label: { it?: string; en?: string };
  position?: number;
}

export interface CreatePollInput {
  question: { it?: string; en?: string };
  options: PollOptionInput[];
  openAt?: string;
  closeAt?: string;
  matchId?: string;
  isActive?: boolean;
  // T-281: notifica di avvio opzionale (one-shot alla creazione).
  sendStartNotification?: boolean;
  notificationTitle?: string;
  notificationSubtitle?: string;
}

export type UpdatePollInput = {
  question?: { it?: string; en?: string };
  openAt?: string;
  closeAt?: string;
  matchId?: string;
  isActive?: boolean;
};

const QK = {
  list: (all: boolean) => ['polls', { all }] as const,
};

export function usePolls(all: boolean = true) {
  return useQuery({
    queryKey: QK.list(all),
    queryFn: async () => {
      const res = await apiClient.get<{ data: PollDto[] }>('/polls', {
        params: all ? { all: true } : {},
      });
      return res.data.data;
    },
  });
}

export function useCreatePoll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatePollInput) => {
      const res = await apiClient.post<PollDto>('/polls', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['polls'] }),
  });
}

export function useUpdatePoll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdatePollInput }) => {
      const res = await apiClient.patch<PollDto>(`/polls/${id}`, input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['polls'] }),
  });
}

export function useDeletePoll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/polls/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['polls'] }),
  });
}
